import React, { useState, useRef } from 'react';
import { useApp } from '../../core/AppContext';
import { SELF_TESTS } from './selfTests';
import ModelEvalView from '../model_eval/ModelEvalView';
import BenchmarkingView from '../benchmarking/BenchmarkingView';
import TsaPersonaView from '../tsa_persona/TsaPersonaView';
import LabVendingView from '../lab_vending/LabVendingView';
import AiIntakeView from '../ai_intake/AiIntakeView';
import AdminView from '../admin/AdminView';

const API_BASE = '/api';
const VIEWS = { eval: ModelEvalView, benchmarking: BenchmarkingView, tsa: TsaPersonaView,
                vending: LabVendingView, intake: AiIntakeView, admin: AdminView };
const COLORS = { pass: '#28a745', fail: '#dc3545', warn: '#fd7e14', skip: '#adb5bd', running: '#0073bb' };
const ICON = { pass: '✔', fail: '✖', warn: '⚠', skip: '–', running: '…' };

// Catches render crashes of a view mounted off-screen.
class Catcher extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err) { this.props.onError(err); }
  render() { return this.state.err ? null : this.props.children; }
}

// Mount a view hidden for `ms`, report whether it rendered without throwing.
function RenderProbe({ View, onDone, ms = 1500 }) {
  const done = useRef(false);
  const finish = (r) => { if (!done.current) { done.current = true; onDone(r); } };
  React.useEffect(() => {
    const t = setTimeout(() => finish({ status: 'pass', detail: `rendered for ${ms} ms without errors` }), ms);
    return () => clearTimeout(t);
  }, []);
  return (
    <div style={{ position: 'absolute', left: '-10000px', top: 0, width: '1200px', height: '800px', overflow: 'hidden' }} aria-hidden="true">
      <Catcher onError={(e) => finish({ status: 'fail', detail: `render error: ${e.message}` })}><View /></Catcher>
    </div>
  );
}

export default function SelfTestView() {
  const { can, getAuthHeaders, role, username } = useApp();
  const [results, setResults] = useState({});       // { 'nav::name': {status, detail} }
  const [running, setRunning] = useState(false);
  const [probe, setProbe] = useState(null);         // { nav, resolve }
  const [lastRun, setLastRun] = useState(null);
  const [includeRender, setIncludeRender] = useState(true);

  const setRes = (k, v) => setResults(prev => ({ ...prev, [k]: v }));

  const get = async (path) => {
    const t0 = performance.now();
    const res = await fetch(`${API_BASE}${path}`, { headers: getAuthHeaders() });
    let data = null;
    try { data = await res.json(); } catch (e) { data = null; }
    return { status: res.status, ok: res.ok, data, ms: Math.round(performance.now() - t0) };
  };

  const renderCheck = (nav) => new Promise((resolve) => setProbe({ nav, resolve }));

  const runMenus = async (menus) => {
    setRunning(true);
    const ctx = { get, can, shared: {} };
    // core/session first so shared values (catalog etc.) are filled in
    const ordered = [...SELF_TESTS.filter(m => m.nav === 'core'), ...menus.filter(m => m.nav !== 'core')];
    for (const m of ordered) {
      if (includeRender && VIEWS[m.nav]) {
        const k = `${m.nav}::Page renders`;
        setRes(k, { status: 'running' });
        setRes(k, await renderCheck(m.nav));
        setProbe(null);
      }
      for (const c of m.checks) {
        const k = `${m.nav}::${c.name}`;
        setRes(k, { status: 'running' });
        try { setRes(k, (await c.run(ctx)) || { status: 'pass' }); }
        catch (e) { setRes(k, { status: 'fail', detail: e.message }); }
      }
    }
    setLastRun(new Date());
    setRunning(false);
  };

  const rowsFor = (m) => [
    ...(VIEWS[m.nav] ? [{ name: 'Page renders' }] : []),
    ...m.checks,
  ];

  const all = Object.values(results);
  const count = (s) => all.filter(r => r.status === s).length;

  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ ran_at: lastRun, user: username, role, results }, null, 2)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `eea-self-test-${new Date().toISOString().slice(0, 19).replace(/:/g, '')}.json`; a.click();
  };

  const btn = { padding: '8px 14px', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>🧪 Self Test</h2>
      <p style={{ color: '#666', fontSize: '13px', marginTop: '-6px' }}>
        Quick, read-only checks for every menu item: each page is rendered off-screen to catch crashes, then its key API calls are tested.
        Nothing is created, changed or deleted. Checks you don't have the privilege for are skipped.
      </p>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap' }}>
        <button disabled={running} onClick={() => { setResults({}); runMenus(SELF_TESTS); }} style={{ ...btn, background: '#0073bb', color: '#fff' }}>
          {running ? 'Running…' : '▶ Run all'}
        </button>
        <label style={{ fontSize: '12px' }}>
          <input type="checkbox" checked={includeRender} onChange={e => setIncludeRender(e.target.checked)} disabled={running} /> Include page render checks
        </label>
        {all.length > 0 && (
          <span style={{ fontSize: '13px' }}>
            <b style={{ color: COLORS.pass }}>{count('pass')} pass</b> · <b style={{ color: COLORS.fail }}>{count('fail')} fail</b> ·{' '}
            <b style={{ color: COLORS.warn }}>{count('warn')} warn</b> · <span style={{ color: '#888' }}>{count('skip')} skipped</span>
            {lastRun && <span style={{ color: '#888' }}> · {lastRun.toLocaleTimeString()}</span>}
          </span>
        )}
        {lastRun && !running && <button onClick={exportJson} style={{ ...btn, background: '#e9ecef' }}>Export JSON</button>}
      </div>

      {SELF_TESTS.map(m => (
        <div key={m.nav} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderBottom: '1px solid #eee', background: '#f8fafc' }}>
            <strong>{m.label}</strong>
            <button disabled={running} onClick={() => runMenus([m])} style={{ ...btn, padding: '4px 10px', fontSize: '11px', background: '#e9ecef' }}>Run</button>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <tbody>
              {rowsFor(m).map(c => {
                const r = results[`${m.nav}::${c.name}`];
                return (
                  <tr key={c.name} style={{ borderBottom: '1px solid #f1f1f1' }}>
                    <td style={{ width: '28px', padding: '6px 12px', color: r ? COLORS[r.status] : '#ccc', fontWeight: 'bold' }}>{r ? ICON[r.status] : '○'}</td>
                    <td style={{ width: '260px', padding: '6px' }}>{c.name}</td>
                    <td style={{ padding: '6px', color: r && r.status === 'fail' ? '#b00020' : '#666', wordBreak: 'break-word' }}>{r ? (r.detail || r.status) : ''}</td>
                  </tr>
                );
              })}
              {rowsFor(m).length === 0 && <tr><td style={{ padding: '6px 12px', color: '#aaa' }} colSpan={3}>No checks</td></tr>}
            </tbody>
          </table>
        </div>
      ))}

      {probe && <RenderProbe key={probe.nav} View={VIEWS[probe.nav]} onDone={probe.resolve} />}
    </div>
  );
}
